import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Copy, Check, ArrowUpRight, Hash } from "lucide-react";
import { toast } from "sonner";
import AdminActions from "@/components/AdminActions";
import VideoEmbed from "@/components/VideoEmbed";
import { fontClass } from "@/lib/fonts";
import LangToggle from "@/components/LangToggle";
import Seo from "@/components/Seo";

export default function PromptDetail() {
  const { slug } = useParams();
  const [prompt, setPrompt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [lang, setLang] = useState("en");

  const load = useCallback(() => {
    api.get(`/prompts/${slug}`).then(r => setPrompt(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (!prompt) return <div className="min-h-screen flex items-center justify-center text-slate-200">Loading…</div>;

  const hasBn = Boolean(prompt.title_bn);
  const bn = lang === "bn" && hasBn;
  const title = bn ? prompt.title_bn : prompt.title;
  const summary = bn ? (prompt.summary_bn || prompt.summary) : prompt.summary;

  const copyPrompt = async () => {
    await navigator.clipboard.writeText(prompt.prompt_text);
    setCopied(true);
    toast.success("Prompt copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen pt-32 relative overflow-hidden">
      <Seo title={prompt.title} description={prompt.summary} path={`/prompts/${prompt.slug}`} />
      <div className="orb orb-magenta w-[26rem] h-[26rem] -top-32 -right-32" aria-hidden="true" />
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-12 relative">
        <Link to="/prompts" className="text-xs text-slate-200 uppercase tracking-[0.2em] hover:text-orange-300 transition-colors">← All Prompts</Link>

        <div className="mt-8 flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <span className="chip">{prompt.category}</span>
              {hasBn && <LangToggle lang={lang} setLang={setLang} />}
            </div>
            <h1 className={`${bn ? "font-bn" : fontClass(prompt.title_font)} font-bold tracking-tighter leading-[1.05] text-4xl sm:text-5xl text-white mt-3`}>{title}</h1>
            <p className={`text-slate-200 mt-5 text-lg leading-relaxed${bn ? " font-bn" : ""}`}>{summary}</p>
          </div>
          <AdminActions kind="prompts" item={prompt} onChange={load} redirectAfterDelete="/prompts" />
        </div>

        <div className="aspect-[16/8] overflow-hidden mt-10 rounded-3xl border border-white/10">
          <img src={prompt.cover_image} alt="" className="w-full h-full object-cover" />
        </div>

        {prompt.video_url && <VideoEmbed url={prompt.video_url} className="mt-6" />}

        <div className="mt-10 glass-panel rounded-3xl overflow-hidden relative">
          <div className="absolute top-0 inset-x-0 h-[2px]" style={{ background: "linear-gradient(90deg, transparent, #EA580C, transparent)" }} aria-hidden="true" />
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-200 font-mono">/ The Prompt</div>
            <button
              onClick={copyPrompt}
              className="text-xs uppercase tracking-widest flex items-center gap-2 text-orange-400 hover:text-white transition-colors font-semibold"
              data-testid="copy-prompt-btn"
            >
              {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
            </button>
          </div>
          <pre className="p-6 md:p-8 font-mono text-sm leading-relaxed text-slate-200 whitespace-pre-wrap" data-testid="prompt-text">
            {prompt.prompt_text}
          </pre>
        </div>

        {prompt.use_cases?.length > 0 && (
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="card-void !rounded-3xl p-7">
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-200 font-semibold mb-4">Use cases</div>
              <ul className="space-y-2.5">
                {prompt.use_cases.map((u) => (
                  <li key={u} className="flex items-center gap-2 text-slate-200"><ArrowUpRight size={14} className="text-orange-400" aria-hidden="true" />{u}</li>
                ))}
              </ul>
            </div>
            <div className="card-void !rounded-3xl p-7">
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-200 font-semibold mb-4">Tags</div>
              <div className="flex flex-wrap gap-2">
                {prompt.tags.map(t => (
                  <span key={t} className="bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-xs uppercase tracking-widest text-slate-200 flex items-center gap-1">
                    <Hash size={10} aria-hidden="true" />{t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
